import React from 'react';
import PropTypes from 'prop-types';
import IconInformasiBesaranSubsidi from '../../components/icons/IconInformasiBesaranSubsidi';
import IconTrash from '../../components/icons/IconTrash';
import BadgeStatus from '../../components/elements/BadgeStatus';
import { formatDate, formatDateRange, formatRupiah } from '../../utils/format';
import BannerRuleSubsidi from './BannerRuleSubsidi';
import styles from './styles.scoped.css';

export default function DataSubsidiContent({
  activeData,
  historyList,
  onOpenDelete,
  meta,
  isLoadMoreLoading,
  onLoadMore,
}) {
  const tableRef = React.useRef(null);

  const checkAndTriggerLoadMore = React.useCallback(
    (target) => {
      if (isLoadMoreLoading || !onLoadMore) return;
      const page = Number(meta?.page || 1);
      const totalPage = Number(meta?.totalPage || meta?.total_page || 1);

      if (page >= totalPage) return;

      if (target) {
        const { scrollHeight, scrollTop, clientHeight } = target;
        if (scrollHeight - scrollTop - clientHeight <= 80) {
          onLoadMore();
          return;
        }
      }

      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom <= windowHeight + 100) {
          onLoadMore();
        }
      }
    },
    [isLoadMoreLoading, meta, onLoadMore]
  );

  const handleScroll = (event) => {
    checkAndTriggerLoadMore(event.currentTarget);
  };

  React.useEffect(() => {
    const handleWindowScroll = () => {
      checkAndTriggerLoadMore(null);
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [checkAndTriggerLoadMore]);

  const scheduledData =
    historyList &&
    historyList.find(
      (item) => item.status === 'SCHEDULED' || item.status === 'Belum Aktif'
    );

  const isValidActiveData = Boolean(
    activeData && (activeData.id || activeData.status || activeData.startDate)
  );

  const activeItem =
    (isValidActiveData ? activeData : null) ||
    (historyList &&
      historyList.find(
        (item) => item.status === 'ACTIVE' || item.status === 'Aktif'
      ));

  const hasHistory = Array.isArray(historyList) && historyList.length > 0;
  const cardData = hasHistory ? (activeItem || scheduledData || historyList[0]) : null;

  let banner = null;
  if (!activeItem && !scheduledData) {
    banner = (
      <BannerRuleSubsidi
        size={40}
        text="Saat ini tidak ada informasi besaran subsidi aktif."
        type="warning"
      />
    );
  } else if (!activeItem && scheduledData) {
    banner = (
      <BannerRuleSubsidi
        size={40}
        text={
          <>
            Saat ini tidak ada informasi besaran subsidi aktif (informasi
            aktif pada tanggal{' '}
            <strong>
              {formatDateRange(
                scheduledData.startDate || scheduledData.tanggalMulai,
                scheduledData.endDate || scheduledData.tanggalBerakhir
              )}
            </strong>
            ).
          </>
        }
        type="warning"
      />
    );
  } else if (activeItem && scheduledData) {
    banner = (
      <BannerRuleSubsidi
        size={40}
        text={
          <>
            Per tanggal{' '}
            <strong>
              {formatDate(scheduledData.startDate || scheduledData.tanggalMulai)}
            </strong>{' '}
            informasi besaran subsidi aktif mengikuti aturan baru{' '}
            <strong>
              {formatRupiah(scheduledData.subsidyAmount)}
            </strong>
            .
          </>
        }
        type="info"
      />
    );
  }

  return (
    <>
      {banner}

      {/* Active / Scheduled / Latest Besaran Subsidi Card */}
      {cardData && (
        <div className={styles.activeInfoCard}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitleWrapper}>
              <IconInformasiBesaranSubsidi color="#5E811E" />
              <h3 className={styles.cardTitle}>Informasi Besaran Subsidi</h3>
            </div>
            <BadgeStatus label={cardData.status} />
          </div>

          <div className={styles.detailGrid}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Besaran subsidi</span>
              <span className={styles.detailSeparator}>:</span>
              <span className={styles.detailValue}>
                {formatRupiah(cardData.subsidyAmount)}
              </span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Tanggal berlaku</span>
              <span className={styles.detailSeparator}>:</span>
              <span className={styles.detailValue}>
                {formatDateRange(
                  cardData.startDate || cardData.tanggalMulai,
                  cardData.endDate || cardData.tanggalBerakhir
                )}
              </span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Dibuat oleh</span>
              <span className={styles.detailSeparator}>:</span>
              <span className={styles.detailValue}>
                {cardData.createdBy || cardData.dibuatOleh || '-'}
              </span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Tanggal Submit</span>
              <span className={styles.detailSeparator}>:</span>
              <span className={styles.detailValue}>
                {formatDate(cardData.createdAt || cardData.tanggalSubmit)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Riwayat Besaran Subsidi Table */}
      {historyList && historyList.length > 0 && (
        <div className={styles.tableWrapper} onScroll={handleScroll} ref={tableRef}>
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>No</th>
                <th>Tanggal Submit</th>
                <th>Besaran Subsidi</th>
                <th>Tanggal Berlaku</th>
                <th>Dibuat Oleh</th>
                <th>Status</th>
                <th>Hapus</th>
              </tr>
            </thead>
            <tbody>
              {historyList.map((item, index) => {
                const isDeletable =
                  item.status === 'SCHEDULED' || item.status === 'Belum Aktif';

                return (
                  <tr key={item.id || item._id || index}>
                    <td>{index + 1}.</td>
                    <td>{formatDate(item.createdAt || item.tanggalSubmit)}</td>
                    <td>{formatRupiah(item.subsidyAmount)}</td>
                    <td>
                      {formatDateRange(
                        item.startDate || item.tanggalMulai,
                        item.endDate || item.tanggalBerakhir
                      )}
                    </td>
                    <td>{item.createdBy || item.dibuatOleh || '-'}</td>
                    <td><BadgeStatus label={item.status} /></td>
                    <td>
                      {isDeletable ? (
                        <button
                          className={styles.btnDeleteActive}
                          onClick={() => onOpenDelete(item)}
                          type="button"
                        >
                          <IconTrash />
                          Hapus
                        </button>
                      ) : (
                        <button
                          className={styles.btnDeleteDisabled}
                          disabled
                          type="button"
                        >
                          <IconTrash />
                          Hapus
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isLoadMoreLoading && (
            <div style={{ padding: '10px 0', textAlign: 'center', fontSize: '13px', color: '#636b75' }}>
              Memuat data lebih banyak...
            </div>
          )}
        </div>
      )}
    </>
  );
}

DataSubsidiContent.propTypes = {
  activeData: PropTypes.object,
  historyList: PropTypes.array,
  isLoadMoreLoading: PropTypes.bool,
  meta: PropTypes.object,
  onLoadMore: PropTypes.func,
  onOpenDelete: PropTypes.func.isRequired,
};

